import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  HostListener,
  Inject,
  Input,
} from '@angular/core';
import { get, isNil } from 'lodash-es';

import { metaKeys } from './constants/meta-keys.const';
import { navigationKeys } from './constants/navigation-keys.const';

@Directive({ standalone: true, selector: 'input[awNumbersOnly]' })
export class NumbersOnlyDirective {
  @Input('awNumbersOnly')
  set enabled(_enabled: BooleanInput) {
    this._enabled = coerceBooleanProperty(_enabled);
  }

  get enabled(): boolean {
    return this._enabled;
  }

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly elementRef: ElementRef<HTMLInputElement>,
  ) {}

  private _enabled = false;

  private get input(): HTMLInputElement {
    return this.elementRef.nativeElement;
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.enabled) {
      return;
    }

    const { key } = event;

    if (isNaN(parseInt(key, 10)) && !this.isAllowedKeyPress(event)) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    if (!this.enabled) {
      return;
    }

    const pastedText = this.extractPastedText(event);

    if (!isNil(pastedText)) {
      this.handlePastedText(pastedText);
      event.preventDefault();
    }
  }

  private isAllowedKeyPress(event: KeyboardEvent): boolean {
    const { key } = event;

    return (
      key === '.' ||
      navigationKeys.indexOf(key) > -1 ||
      this.isMetaCombination(event)
    );
  }

  private isMetaCombination(event: KeyboardEvent): boolean {
    const { key, ctrlKey, metaKey } = event;

    return metaKeys.includes(key) && (ctrlKey || metaKey);
  }

  private extractPastedText(event: ClipboardEvent): string | undefined {
    const windowClipboardData = get(window, 'clipboardData') as any;

    if (windowClipboardData) {
      return windowClipboardData.getData('text');
    }

    const eventClipboardData = get(event, 'clipboardData');

    if (eventClipboardData) {
      return eventClipboardData?.getData('text/plain');
    }

    return undefined;
  }

  private handlePastedText(pastedText: string): void {
    const sanitizedText = this.sanitizeText(pastedText);
    const pasted = this.document.execCommand(
      'insertText',
      false,
      sanitizedText,
    );

    if (!pasted) {
      this.insertPastedText(sanitizedText);
    }
  }

  private sanitizeText(input: string): string {
    const regex = new RegExp('[^0-9]', 'g');

    return input.replace(regex, '');
  }

  private insertPastedText(text: string): void {
    const { input } = this;

    const { selectionStart: start, selectionEnd: end } = input;

    input.setRangeText(text, start ?? 0, end ?? 0, 'end');

    this.dispatchInputEvent(input);
  }

  // Angular's Reactive Form relies on "input" event, but on Firefox the setRangeText method doesn't trigger it
  private readonly dispatchInputEvent = (input: HTMLInputElement): void => {
    const shouldDispatch = get(window, 'InstallTrigger', false);

    if (shouldDispatch) {
      input.dispatchEvent(new Event('input', { cancelable: true }));
    }
  };
}
